import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material';
import { undefined } from 'zod';

import { qrcodeAPIService } from '~/api/qrcode';
import { DownloadQrStep } from '~/components/builder/DownloadQrStep/DownloadQrStep';
import { PreviewScreen } from '~/components/organisms/previewBar/components/utils/PreviewScreen';
import { MobilePreviewBarLayout } from '~/components/organisms/previewBar/mobile/MobilePreviewBarLayout';
import { QRPreviewBar } from '~/components/organisms/previewBar/mobile/QRPreviewBar';
import PreviewBar from '~/components/organisms/previewBar/PreviewBar';
import { resetAllMicrositesReducersToInitialState } from '~/components/organisms/previewBar/utils';
import { INITIAL_STEP } from '~/components/organisms/stepper/constants';
import MobileFooterStepper from '~/components/organisms/stepper/MobileFooterStepper';
import { NEW_QR_CODES_TEMPLATE_IDS } from '~/constants/common';
import { BUILDER_BOTTOM_NAVIGATION_ID, DESKTOP_MIN_WIDTH } from '~/constants/common';
import {
  QR_CODE_BUILDER_CREATED_ID_PARAM_NAME,
  QR_CODE_ID_PARAM_NAME,
  QR_CODE_TEMPLATE_PARAM_NAME,
  QRCODE_STATUS,
} from '~/constants/qr-code';
import { templates } from '~/constants/queryParams';
import { PATH } from '~/constants/route-path';
import useQrCodeData from '~/hooks/useQrCodeData';
import { useAppDispatch } from '~/hooks/useRedux';
import { ConfirmLeaveEditingQr } from '~/pages/CreateQR/components/ConfirmLeaveEditingQr';
import CreateQrMain from '~/pages/CreateQR/components/CreateQrMain';
import NavigationBottom from '~/pages/CreateQR/components/NavigationBottom';
import { createQrCodeStylesThunk, updateQrCodeThunk } from '~/store/reducers/qr-code';
import { qrCodeSlice } from '~/store/reducers/qr-code';
import { getQrCodeReducerData } from '~/store/reducers/qr-code/selectors';
import { stepperSlice } from '~/store/reducers/stepepr';
import { getStepperData } from '~/store/reducers/stepepr/selectors';
import useBuilderStore from '~/store/useBuilderStore';
import { logger } from '~/utils/logger';

import { previewModelData } from '../MyMicrosite/utils/model-data';

import Stepper from './components/Stepper';

const stepperActions = stepperSlice.actions;
const stepsLength = 2;

const CreateQR = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const qrCodeReducer = useSelector(getQrCodeReducerData) || [];

  const theme = useTheme();

  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));

  const { setIsMobilePreviewOpened, isMobilePreviewOpened, activeStep, setActiveStep } = useBuilderStore();

  const { redirectRoute } = useSelector(getStepperData);
  const qrCodeState = useSelector(getQrCodeReducerData);

  const [showQRModal, setShowQRModal] = useState(false);
  const [isLoadingQrData, setIsLoadingQRData] = useState(false); // TODO should be deleted once old flow will be removed

  const templateId = searchParams.get(QR_CODE_TEMPLATE_PARAM_NAME) || null;

  const createdId = searchParams.get(QR_CODE_BUILDER_CREATED_ID_PARAM_NAME) || null;
  const isNewQrFlow = NEW_QR_CODES_TEMPLATE_IDS.includes(templateId);
  const qrCodeId = searchParams.get(QR_CODE_ID_PARAM_NAME);

  const { data: newFlowQrCodeData, isLoading: isLoadingNewFlowQrCodeData } = useQrCodeData({
    qrCodeId: isNewQrFlow ? qrCodeId : null,
  });

  const onAcceptRedirect = (redirectPath?: string) => {
    debugger;
    if (redirectRoute) {
      navigate(redirectRoute);
    }

    dispatch(stepperActions.setRedirectRoute(''));
    // TODO need to fix properly, below there is useEffect with setSearchParams(''), it's affect navigation to my qrs page (ticket - 1 step is shown after creation QR)
    setTimeout(() => {
      setActiveStep(INITIAL_STEP);
    }, 500);
    dispatch(qrCodeSlice.actions.resetState());
    resetAllMicrositesReducersToInitialState(dispatch);

    if (redirectPath && typeof redirectPath === 'string' && redirectPath.length > 0) {
      navigate(redirectPath);
    }
  };

  const handleCloseDownloadModal = () => {
    setShowDownloadModal(false);
    onAcceptRedirect(PATH.QR.MAIN);
  };

  const handleNext = async () => {
    try {
      if (activeStep === 2) {
        window.dataLayer?.push({
          event: 'create_qr_code_completed',
          qr_code_id: qrCodeId,
          qr_code_type: templateId,
        });
        await dispatch(updateQrCodeThunk({ status: QRCODE_STATUS.DONE }));
        setActiveStep(3);
        setShowDownloadModal(true);

        return;
      }

      if (activeStep === 1 && !createdId) {
        await dispatch(updateQrCodeThunk({ status: QRCODE_STATUS.DRAFT }));
        window.dataLayer?.push({
          event: 'create_qr_code',
          qr_code_id: undefined,
          qr_code_type: qrCodeState?.data?.micrositeType,
        });
      }

      if (activeStep >= stepsLength) return;

      setActiveStep(activeStep + 1);
    } catch (error) {
      logger.log('fail on click next', error);
    }
  };

  const handleBack = () => {
    if (activeStep === 1) {
      dispatch(stepperActions.setRedirectRoute(PATH.QR.CREATE));

      return;
    }
    if (activeStep <= 0) return;
    setActiveStep(activeStep - 1);
  };

  const loadEditData = async () => {
    try {
      setActiveStep(1);
      setIsLoadingQRData(true);
      dispatch(createQrCodeStylesThunk(true));
      const response = await qrcodeAPIService.getQrCodeById(createdId);

      previewModelData[response.data?.micrositeType](response.data?.microsite, dispatch, response.data);
    } catch (e) {
      logger.error(e);
    } finally {
      setIsLoadingQRData(false);
    }
  };

  const handleStep = async (selectedStep) => {
    if (activeStep === 0 && !createdId) {
      setSearchParams('');
      return;
    }
    if (selectedStep === 1 && createdId) {
      await loadEditData();
      return;
    }

    setActiveStep(selectedStep);
  };

  useEffect(() => {
    if (!activeStep && !createdId) {
      setSearchParams('');
    }
  }, [activeStep]);

  useEffect(() => {
    // TODO what it is? remove it
    if (`${window.location.origin}${PATH.QR.CREATE}` === window.location.href) {
      setActiveStep(0);
    }
  }, [window.location.href]);

  useEffect(() => {
    if (createdId) {
      if (isNewQrFlow) {
        if (activeStep !== 1) {
          setActiveStep(1);
        }
      } else {
        loadEditData();
      }
    }

    dispatch(qrCodeSlice.actions.setSelectedQr(null));

    return () => {
      dispatch(qrCodeSlice.actions.resetState());
    };
  }, []);

  return (
    <StyledContainer>
      {isDesktop && <Stepper handleStep={handleStep} />}
      <Box
        sx={{
          display: 'flex',
          marginTop: isDesktop ? '24px' : undefined,
          gap: isNewQrFlow ? undefined : '24px',
          flex: 1,
          paddingBottom: activeStep === 0 ? '24px' : undefined,
        }}
      >
        {isNewQrFlow ? (
          <CreateQrMain isNewQrFlow />
        ) : (
          <>
            {/* TODO entire block below should be deleted once old flow will be rewritten */}
            {isLoadingQrData && activeStep === 1 ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <CreateQrMain isNewQrFlow={false} />
                <>
                  {isDesktop ? (
                    <PreviewBar templateId={String(templates.indexOf(templateId))} />
                  ) : (
                    <MobilePreviewBarLayout
                      isOpen={isMobilePreviewOpened}
                      onClose={() => setIsMobilePreviewOpened(false)}
                    >
                      <PreviewScreen templateId={String(templates.indexOf(templateId))} />
                    </MobilePreviewBarLayout>
                  )}
                </>
              </>
            )}
          </>
        )}

        {!isDesktop && !!activeStep && (
          <>
            <QRPreviewBar
              onClose={() => setShowQRModal(false)}
              isOpen={showQRModal}
              qrImageUrl={isNewQrFlow ? newFlowQrCodeData?.qrImage : qrCodeState?.data?.qrImage}
              isQrLoading={isNewQrFlow ? isLoadingNewFlowQrCodeData : !!qrCodeState?.loading}
            />
            <MobileFooterStepper
              handleBack={handleBack}
              handleNext={handleNext}
              showQRModal={() => setShowQRModal(true)}
              showPreviewModal={() => setIsMobilePreviewOpened(true)}
              activeStep={activeStep}
            />
          </>
        )}
      </Box>
      {isNewQrFlow && <Box id={BUILDER_BOTTOM_NAVIGATION_ID} sx={{ display: 'contents' }} />}
      {isDesktop && !isNewQrFlow && (
        <NavigationBottom
          handleNext={handleNext}
          handleBack={handleBack}
          handleStep={handleStep}
          isNextProcessing={qrCodeState.loadingSet.isUpdateQrcodeLoading}
        />
      )}

      <ConfirmLeaveEditingQr
        isOpen={!!redirectRoute}
        onAccept={onAcceptRedirect}
        onClose={() => dispatch(stepperActions.setRedirectRoute(''))}
      />
      {showDownloadModal && (
        <DownloadQrStep
          onClose={handleCloseDownloadModal}
          open={showDownloadModal}
          id={qrCodeReducer.data.id}
          imageName={qrCodeReducer.data.name}
        />
      )}
    </StyledContainer>
  );
};

export default CreateQR;

export const StyledContainer = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    paddingTop: '20px',
    paddingRight: '10px',
    minHeight: '100vh',
    [theme.breakpoints.down(DESKTOP_MIN_WIDTH)]: {
      padding: '24px 16px 50px',
    },
  };
});
